<template>
  <div class="common-part-list">
    <p class="hint">
      {{
        '소모품 기본정보(프린터 모델·제조사·구분·색상·수집 설명)에 공용 품번을 등록합니다. 임대업체 자체 매핑이 없을 때만 이 값을 씁니다.'
          | translate
      }}
    </p>
    <div class="tools">
      <input
        v-model="keyword"
        type="text"
        class="search-input"
        :placeholder="'품번 / 모델 / 제조사 / 설명 검색' | translate"
        @keydown.enter.prevent="search"
      />
      <button type="button" class="btn" @click="search">{{ '검색' | translate }}</button>
    </div>
    <div class="table-wrap">
      <table class="list-table">
        <thead>
          <tr>
            <th>{{ '프린터 모델' | translate }}</th>
            <th>{{ '제조사' | translate }}</th>
            <th>{{ '구분' | translate }}</th>
            <th>{{ '색상' | translate }}</th>
            <th>{{ '수집 설명' | translate }}</th>
            <th>{{ '공용 품번' | translate }}</th>
            <th>{{ '수정일시' | translate }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td colspan="8" class="empty">{{ '등록된 공용 품번이 없습니다.' | translate }}</td>
          </tr>
          <tr v-for="row in rows" :key="row.supplyCommonPartId || row.descriptionNorm">
            <td>{{ row.printerModelName || '-' }}</td>
            <td>{{ row.manufacturerName || '-' }}</td>
            <td>{{ categoryLabel(row.supplyCategory) }}</td>
            <td>{{ colorLabel(row.colorCode) }}</td>
            <td class="desc" :title="row.descriptionNorm">{{ row.descriptionNorm || '-' }}</td>
            <td>{{ row.partNumber || '-' }}</td>
            <td class="nowrap">{{ row.updateDatetime || row.registerDatetime || '-' }}</td>
            <td>
              <button type="button" class="link-btn" @click="$emit('edit', toContext(row))">
                {{ '수정' | translate }}
              </button>
              <button type="button" class="link-btn danger" @click="remove(row)">
                {{ '해제' | translate }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="pager">
      <button type="button" class="btn outline" :disabled="page <= 1" @click="changePage(page - 1)">
        {{ '이전' | translate }}
      </button>
      <span>{{ page }} / {{ totalPages }}</span>
      <button
        type="button"
        class="btn outline"
        :disabled="page >= totalPages"
        @click="changePage(page + 1)"
      >
        {{ '다음' | translate }}
      </button>
    </div>
  </div>
</template>

<script>
import { PATH } from '@/constants'

export default {
  name: 'SupplyCommonPartPanel',
  props: {
    refreshKey: { type: Number, default: 0 },
  },
  data() {
    return {
      keyword: '',
      page: 1,
      rowsPerPage: 50,
      totalCount: 0,
      rows: [],
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.totalCount || 0) / this.rowsPerPage))
    },
  },
  watch: {
    refreshKey() {
      this.load()
    },
  },
  created() {
    this.load()
  },
  methods: {
    t(text) {
      return this.$options.filters && this.$options.filters.translate
        ? this.$options.filters.translate(text)
        : text
    },
    categoryLabel(v) {
      const map = {
        TONER: this.t('토너'),
        DRUM: this.t('드럼'),
        WASTE: this.t('폐토너'),
        OTHER: this.t('기타'),
      }
      return map[v] || v || '-'
    },
    colorLabel(color) {
      if (!color || String(color).toUpperCase() === 'UNKNOWN') return '-'
      return color
    },
    toContext(row) {
      return {
        printerModelId: row.printerModelId || 0,
        printerModelName: row.printerModelName || '',
        manufacturerName: row.manufacturerName || '',
        supplyCategory: row.supplyCategory || 'OTHER',
        colorCode: row.colorCode || 'UNKNOWN',
        description: row.descriptionNorm || '',
        partNumber: row.partNumber || '',
      }
    },
    search() {
      this.page = 1
      this.load()
    },
    changePage(next) {
      this.page = next
      this.load()
    },
    async load() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_COMMON_PART_LIST)
          .setObject({
            keyword: this.keyword || null,
            page: this.page,
            rows: this.rowsPerPage,
          })
          .enqueue()
        const data = res?.data || res || {}
        this.rows = Array.isArray(data.list) ? data.list : []
        this.totalCount = Number(data.totalCount || 0)
      } catch (e) {
        console.error(e)
        this.rows = []
        this.totalCount = 0
      }
    },
    async remove(row) {
      try {
        await this.$request(PATH.MONITORING.SUPPLY_COMMON_PART_DELETE)
          .setObject(this.toContext(row))
          .catch()
          .enqueue()
        this.$toasted?.show?.(this.t('공용 품번 매핑을 해제했습니다.'))
        this.load()
        this.$emit('deleted')
      } catch (e) {
        console.error(e)
        this.$toasted?.error?.(this.t('매핑 해제에 실패했습니다.'))
      }
    },
  },
}
</script>

<style scoped>
.hint {
  margin: 0 0 12px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
.tools {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}
.search-input {
  min-width: 280px;
  height: 32px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 10px;
}
.btn {
  height: 32px;
  border: 0;
  border-radius: 4px;
  background: #2563eb;
  color: #fff;
  padding: 0 12px;
  cursor: pointer;
  font-weight: 600;
}
.btn.outline {
  background: #fff;
  color: #334155;
  border: 1px solid #cbd5e1;
}
.btn.outline:disabled {
  opacity: 0.5;
  cursor: default;
}
.table-wrap {
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.list-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.list-table th,
.list-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  white-space: nowrap;
}
.list-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}
.list-table td.empty {
  text-align: center;
  color: #94a3b8;
  padding: 24px;
}
.list-table td.desc {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nowrap {
  white-space: nowrap;
}
.link-btn {
  border: 0;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
  padding: 0 6px;
  font-size: 12px;
}
.link-btn.danger {
  color: #dc2626;
}
.pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  font-size: 13px;
  color: #475569;
}
</style>
